import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import Box from "@mui/material/Box";
import SubscriptionInformation from "./components/SubscriptionInformation";
import InvoicesTable from "./components/InvoicesTable";
import {
  startGetPayments,
  startGetPayInvoiceUrl,
} from "../../actions/payments";
import ModalInvoice from "./components/ModalInvoice";
import { getPathDatabaseByRole } from "../../actions/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { hasRoleCapability } from "../../utils/roleCapabilities";
import { ALERT_ICON_TYPE_SUCCESS } from "../../utils/constants";
import ContainerPage from "../../components/Containers/ContainerPage";
import {
  BILLING_AND_PAYMENTS_TITLE,
  BILLING_SPECIALIST_PURCHASE_SUCCESS_DESCRIPTION,
  BILLING_SPECIALIST_PURCHASE_SUCCESS_TITLE,
  BILLING_SPECIALIST_SCHEDULE_MEETING_BUTTON,
  MODAL_BUY_CREDITS_PURCHASED,
  MODAL_BUY_CREDITS_PURCHASED_DESCRIPTION,
  MODAL_BUY_VIDEOS_PURCHASED,
  MODAL_BUY_VIDEOS_PURCHASED_DESCRIPTION,
} from "../../i18n/keysTranslations";
import TitlePage from "../../components/Texts/TitlePage";
import { useTranslationApp } from "../../lib/i18next";
import { ConfirmAlert, SimpleAlert } from "../../components/Alerts/Alerts";
import SuccessConfetti from "../../components/Common/SuccessConfetti";
import { getLanguageFormatted, openLink } from "../../utils/string";
import i18next from "i18next";
import { getLastPendingPackagePlanInvoiceEntry } from "../../utils/pendingPackagePlanInvoice";

const CAL_ONBOARDING_ES =
  "https://cal.com/team/customer-success-adle/onboarding-adle";
const CAL_ONBOARDING_EN =
  "https://cal.com/team/customer-success-adle/adle-initial-onboarding";

const BillingAndPayments = () => {
  const { t } = useTranslationApp();
  const [dataInvoices, setDataInvoices] = useState({});
  const [invoiceSelected, setInvoiceSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [modalInvoiceOpen, setModalInvoiceOpen] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [params, setParams] = useSearchParams();
  const [payPendingLoading, setPayPendingLoading] = useState(false);

  const dispatch = useDispatchApp();
  const { payments, isPlanActive } = useSelectorApp((state) => state.payments);
  const { userID, role } = dispatch(getPathDatabaseByRole());
  const navigate = useNavigate();

  const getInitialData = () => {
    if (!hasRoleCapability(role, "canEditBilling")) {
      navigate("/");
      return;
    }
    setIsLoading(true);
    setDataInvoices({});
    dispatch(
      startGetPayments({
        onCallbackFinish: () => setIsLoading(false),
      })
    );
  };

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line
  }, [userID]);

  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [showConfetti]);

  useEffect(() => {
    const creditsPurchase = params.get("creditsPurchase");
    const videosPurchase = params.get("videosPurchase");
    const specialistPurchase = params.get("specialistPurchase");

    if (creditsPurchase === "true") {
      SimpleAlert({
        title: t(MODAL_BUY_CREDITS_PURCHASED),
        text: t(MODAL_BUY_CREDITS_PURCHASED_DESCRIPTION),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      setShowConfetti(true);
      params.delete("creditsPurchase");
      setParams(params);
    }

    if (videosPurchase === "true") {
      SimpleAlert({
        title: t(MODAL_BUY_VIDEOS_PURCHASED),
        text: t(MODAL_BUY_VIDEOS_PURCHASED_DESCRIPTION),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      setShowConfetti(true);
      params.delete("videosPurchase");
      setParams(params);
    }

    if (specialistPurchase === "true" || specialistPurchase === "false") {
      const next = new URLSearchParams(params);
      next.delete("specialistPurchase");
      setParams(next, { replace: true });
    }

    if (specialistPurchase === "true") {
      (async () => {
        const result = await ConfirmAlert({
          title: t(BILLING_SPECIALIST_PURCHASE_SUCCESS_TITLE),
          text: t(BILLING_SPECIALIST_PURCHASE_SUCCESS_DESCRIPTION),
          icon: ALERT_ICON_TYPE_SUCCESS,
          showCancelButton: false,
          confirmButtonText: t(BILLING_SPECIALIST_SCHEDULE_MEETING_BUTTON),
        });
        if (result.isConfirmed) {
          const calOnboardingUrl = getLanguageFormatted(i18next.language);
          openLink(
            calOnboardingUrl === "es" ? CAL_ONBOARDING_ES : CAL_ONBOARDING_EN
          );
        }
      })();
    }
    // eslint-disable-next-line
  }, [params]);

  useEffect(() => {
    if (isLoading) return;

    let filteredData = {};
    if (Object.keys(payments).length > 0) {
      Object.keys(payments).map((key) => {
        return (filteredData[key] = {
          ...payments[key],
          paidDate: payments[key].paidDate
            ? new Date(payments[key].paidDate)
            : "",
          dueDate: payments[key].dueDate ? new Date(payments[key].dueDate) : "",
        });
      });
    }

    setDataInvoices(filteredData);
  }, [payments, isLoading]);

  const pendingPackagePlanEntry = useMemo(() => {
    if (isLoading || isPlanActive) return null;
    return getLastPendingPackagePlanInvoiceEntry(payments);
  }, [payments, isPlanActive, isLoading]);

  const isPendingSubscriptionPaymentMode = pendingPackagePlanEntry !== null;
  const pendingSubscriptionInvoiceKey =
    pendingPackagePlanEntry?.invoiceKey ?? null;

  const pendingSubscriptionInvoice = useMemo(() => {
    if (!pendingSubscriptionInvoiceKey) return null;
    return (
      dataInvoices[pendingSubscriptionInvoiceKey] ??
      payments[pendingSubscriptionInvoiceKey] ??
      null
    );
  }, [dataInvoices, pendingSubscriptionInvoiceKey, payments]);

  const onPayPendingSubscription = useCallback(async () => {
    if (!pendingSubscriptionInvoiceKey) return;
    setPayPendingLoading(true);
    try {
      const row =
        dataInvoices[pendingSubscriptionInvoiceKey] ??
        payments[pendingSubscriptionInvoiceKey];
      const paymentIntent = row?.paymentID ?? pendingSubscriptionInvoiceKey;
      const result = await dispatch(
        startGetPayInvoiceUrl({ paymentIntent, coldBoot: false })
      );
      window.location.href = result.data.url;
    } finally {
      setPayPendingLoading(false);
    }
  }, [dataInvoices, payments, pendingSubscriptionInvoiceKey, dispatch]);

  const onChangeStatusModalInvoice = (status, invoice = null) => {
    setModalInvoiceOpen(status);
    setInvoiceSelected(invoice);
  };

  return (
    <>
      <SuccessConfetti fire={showConfetti} />
      <ContainerPage
        mode="padding"
        hasMarginBottom={true}
        sx={{ px: 5, pt: 5 }}
      >
        <TitlePage>{t(BILLING_AND_PAYMENTS_TITLE)}</TitlePage>
        <Box
          sx={{
            mt: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            width: "100%",
            maxWidth: {
              xs: "100%",
              md: "calc(100vw - 258px)",
            },
          }}
        >
          <Box
            sx={{
              width: "100%",
              mb: 3,
              display: "flex",
              gap: 2,
              flexDirection: {
                xs: "column",
                lg: "row",
              },
              justifyContent: "space-between",
            }}
          >
            <SubscriptionInformation
              isPendingSubscriptionPaymentMode={
                isPendingSubscriptionPaymentMode
              }
              pendingSubscriptionInvoice={pendingSubscriptionInvoice}
              onPayPendingSubscription={onPayPendingSubscription}
              payPendingLoading={payPendingLoading}
            />
          </Box>
          <Box
            sx={{
              width: "100%",
            }}
          >
            <InvoicesTable
              data={dataInvoices}
              onSelectInvoice={(invoice) =>
                onChangeStatusModalInvoice(true, invoice)
              }
            />
          </Box>

          {modalInvoiceOpen && (
            <ModalInvoice
              data={dataInvoices[invoiceSelected]}
              invoice={invoiceSelected}
              modalOpen={modalInvoiceOpen}
              onCloseModal={() => onChangeStatusModalInvoice(false)}
            />
          )}
        </Box>
      </ContainerPage>
    </>
  );
};

export default BillingAndPayments;
